import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  InputAdornment,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Chip,
  IconButton,
  Divider,
  CircularProgress,
  Popper,
  ClickAwayListener,
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  DirectionsCar as VehicleIcon,
  Assignment as BookingIcon,
  DriveEta as DriverIcon,
  History as HistoryIcon,
  TrendingUp as TrendingIcon,
  Clear as ClearIcon,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { 
  selectSearchQuery, 
  selectSearchResults, 
  selectRecentSearches, 
  selectIsSearching, 
  selectIsSearchOpen,
  selectSelectedIndex,
  setQuery, 
  setResults, 
  setIsSearching, 
  setIsOpen,
  setSelectedIndex,
  clearResults,
  removeRecentSearch,
  type SearchResultItem
} from '@/store/slices/searchSlice';
import { performGlobalSearch, getSearchSuggestions } from '@/utils/globalSearch';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from '@/hooks/useDebounce';
import { initializeStoreWithSampleData } from '@/utils/initializeStore';

// Import selectors for data
import { selectUsers } from '@/store/slices/usersSlice';
import { selectVehicles } from '@/store/slices/vehiclesSlice';
import { selectBookings } from '@/store/slices/bookingsSlice';
import { selectDrivers } from '@/store/slices/driversSlice';

const getTypeIcon = (type: string) => {
  switch (type) {
    case 'user': return <PersonIcon fontSize="small" />;
    case 'vehicle': return <VehicleIcon fontSize="small" />;
    case 'booking': return <BookingIcon fontSize="small" />;
    case 'driver': return <DriverIcon fontSize="small" />;
    default: return <SearchIcon fontSize="small" />;
  }
};

const getTypeColor = (type: string) => {
  switch (type) {
    case 'user': return 'primary';
    case 'vehicle': return 'success';
    case 'booking': return 'warning';
    case 'driver': return 'info';
    default: return 'default';
  }
};

export const GlobalSearch: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Selectors
  const query = useAppSelector(selectSearchQuery);
  const results = useAppSelector(selectSearchResults);
  const recentSearches = useAppSelector(selectRecentSearches);
  const isSearching = useAppSelector(selectIsSearching);
  const isOpen = useAppSelector(selectIsSearchOpen);
  const selectedIndex = useAppSelector(selectSelectedIndex);
  
  // Data selectors
  const users = useAppSelector(selectUsers);
  const vehicles = useAppSelector(selectVehicles);
  const bookings = useAppSelector(selectBookings);
  const drivers = useAppSelector(selectDrivers);
  
  // Debounced search query
  const debouncedQuery = useDebounce(query, 300);
  
  // State for suggestions
  const [suggestions, setSuggestions] = useState<string[]>([]);
  
  // Initialize store with sample data on mount
  useEffect(() => {
    initializeStoreWithSampleData();
  }, []);
  
  // Search effect
  useEffect(() => {
    if (debouncedQuery.length >= 2) {
      dispatch(setIsSearching(true));
      
      // Perform search
      const searchResults = performGlobalSearch(debouncedQuery, {
        users: users.users || [],
        vehicles: vehicles.vehicles || [],
        bookings: bookings.bookings || [],
        drivers: drivers.drivers || [],
      });
      
      dispatch(setResults(searchResults));
      dispatch(setIsSearching(false));
    } else {
      dispatch(setResults([]));
      dispatch(setIsSearching(false));
    }
  }, [debouncedQuery, users, vehicles, bookings, drivers, dispatch]);
  
  // Update suggestions when query changes
  useEffect(() => {
    const newSuggestions = getSearchSuggestions(recentSearches, query);
    setSuggestions(newSuggestions);
  }, [query, recentSearches]);
  
  // Handle input change
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    dispatch(setQuery(value));
    
    if (value.length > 0) {
      dispatch(setIsOpen(true));
    } else {
      dispatch(setIsOpen(false));
      dispatch(clearResults());
    }
  };
  
  // Handle input focus
  const handleInputFocus = () => {
    if (query.length > 0 || recentSearches.length > 0) {
      dispatch(setIsOpen(true));
    }
  };
  
  // Handle click away
  const handleClickAway = () => {
    dispatch(setIsOpen(false));
  };
  
  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!isOpen) return;
    
    const allItems = [...suggestions, ...results];
    const maxIndex = allItems.length - 1;
    
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        dispatch(setSelectedIndex(selectedIndex < maxIndex ? selectedIndex + 1 : 0));
        break;
      case 'ArrowUp':
        event.preventDefault();
        dispatch(setSelectedIndex(selectedIndex > 0 ? selectedIndex - 1 : maxIndex));
        break;
      case 'Enter':
        event.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          // Selected a suggestion
          const suggestion = suggestions[selectedIndex];
          dispatch(setQuery(suggestion));
          inputRef.current?.focus();
        } else if (selectedIndex >= suggestions.length) {
          // Selected a search result
          const resultIndex = selectedIndex - suggestions.length;
          const result = results[resultIndex];
          if (result) {
            handleResultClick(result);
          }
        }
        break;
      case 'Escape':
        dispatch(setIsOpen(false));
        inputRef.current?.blur();
        break;
    }
  };
  
  // Handle result click
  const handleResultClick = (result: SearchResultItem) => {
    dispatch(setIsOpen(false));
    dispatch(clearResults());
    navigate(result.path);
  };
  
  // Handle suggestion click
  const handleSuggestionClick = (suggestion: string) => {
    dispatch(setQuery(suggestion));
    inputRef.current?.focus();
  };
  
  // Handle clear search
  const handleClearSearch = () => {
    dispatch(clearResults());
    dispatch(setIsOpen(false));
    inputRef.current?.focus();
  };
  
  // Handle remove recent search
  const handleRemoveRecentSearch = (search: string, event: React.MouseEvent) => {
    event.stopPropagation();
    dispatch(removeRecentSearch(search));
  };
  
  // Get display items for rendering
  const getDisplayItems = () => {
    const items = [];
    
    // Add suggestions
    if (query.length === 0 && recentSearches.length > 0) {
      items.push({
        type: 'section',
        title: 'Recent Searches',
        items: recentSearches.slice(0, 5).map((search, index) => ({
          id: `recent-${index}`,
          type: 'suggestion',
          title: search,
          icon: <HistoryIcon fontSize="small" />,
          onRemove: (event: React.MouseEvent) => handleRemoveRecentSearch(search, event),
        })),
      });
    }
    
    if (query.length > 0 && suggestions.length > 0) {
      items.push({
        type: 'section',
        title: 'Suggestions',
        items: suggestions.map((suggestion, index) => ({
          id: `suggestion-${index}`,
          type: 'suggestion',
          title: suggestion,
          icon: <TrendingIcon fontSize="small" />,
        })),
      });
    }
    
    // Add search results
    if (results.length > 0) {
      const groupedResults = results.reduce((acc, result) => {
        if (!acc[result.type]) {
          acc[result.type] = [];
        }
        acc[result.type].push(result);
        return acc;
      }, {} as Record<string, SearchResultItem[]>);
      
      Object.entries(groupedResults).forEach(([type, typeResults]) => {
        items.push({
          type: 'section',
          title: `${type.charAt(0).toUpperCase() + type.slice(1)}s (${typeResults.length})`,
          items: typeResults.map(result => ({
            id: result.id,
            type: 'result',
            result,
          })),
        });
      });
    }
    
    return items;
  };
  
  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ flexGrow: 1, maxWidth: 400, position: 'relative' }} ref={searchRef}>
        <TextField
          ref={inputRef}
          size="small"
          placeholder="Search users, vehicles, bookings, drivers..."
          variant="outlined"
          fullWidth
          value={query}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {isSearching ? (
                  <CircularProgress size={20} />
                ) : (
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                )}
              </InputAdornment>
            ),
            endAdornment: query.length > 0 && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'action.hover',
              '&:hover': {
                backgroundColor: 'action.selected',
              },
              '&.Mui-focused': {
                backgroundColor: 'background.paper',
              },
            },
          }}
        />
        
        {/* Search Results Dropdown */}
        <Popper
          open={isOpen}
          anchorEl={searchRef.current}
          placement="bottom-start"
          style={{ width: searchRef.current?.offsetWidth, zIndex: 1300 }}
        >
          <Paper
            sx={{
              mt: 1,
              maxHeight: 400,
              overflow: 'auto',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {getDisplayItems().map((section, sectionIndex) => (
              <Box key={sectionIndex}>
                <Typography
                  variant="caption"
                  sx={{
                    px: 2,
                    py: 1,
                    display: 'block',
                    backgroundColor: 'grey.50',
                    fontWeight: 600,
                    color: 'text.secondary',
                  }}
                >
                  {section.title}
                </Typography>
                
                <List dense>
                  {section.items.map((item, itemIndex) => {
                    const globalIndex = sectionIndex * 10 + itemIndex; // Simple index calculation
                    const isSelected = selectedIndex === globalIndex;
                    
                    if (item.type === 'suggestion') {
                      return (
                        <ListItem
                          key={item.id}
                          button
                          selected={isSelected}
                          onClick={() => handleSuggestionClick(item.title)}
                          sx={{
                            py: 1,
                            '&:hover': {
                              backgroundColor: 'action.hover',
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={item.title}
                            primaryTypographyProps={{
                              variant: 'body2',
                            }}
                          />
                          {item.onRemove && (
                            <IconButton
                              size="small"
                              onClick={item.onRemove}
                              sx={{ ml: 1 }}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          )}
                        </ListItem>
                      );
                    } else if (item.type === 'result') {
                      const result = item.result;
                      return (
                        <ListItem
                          key={result.id}
                          button
                          selected={isSelected}
                          onClick={() => handleResultClick(result)}
                          sx={{
                            py: 1,
                            '&:hover': {
                              backgroundColor: 'action.hover',
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {getTypeIcon(result.type)}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" noWrap>
                                  {result.title}
                                </Typography>
                                <Chip
                                  label={result.type}
                                  size="small"
                                  color={getTypeColor(result.type) as any}
                                  variant="outlined"
                                />
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  {result.subtitle}
                                </Typography>
                                {result.description && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {result.description}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      );
                    }
                    
                    return null;
                  })}
                </List>
                
                {sectionIndex < getDisplayItems().length - 1 && <Divider />}
              </Box>
            ))}
            
            {/* Empty state */}
            {query.length >= 2 && results.length === 0 && !isSearching && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No results found for "{query}"
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Try different keywords or check your spelling
                </Typography>
              </Box>
            )}
            
            {/* Keyboard shortcuts hint */}
            <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider', backgroundColor: 'grey.50' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ArrowUpward fontSize="small" />
                <ArrowDownward fontSize="small" />
                to navigate • Enter to select • Esc to close
              </Typography>
            </Box>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};